import React, { Dispatch, SetStateAction, useContext } from 'react'
import { useIntl } from 'react-intl'
import * as Yup from 'yup'
import { Form, Formik, FormikHelpers } from 'formik'

import { useAppDispatch } from 'app/hooks'
import ConfirmModal from 'components/Modal/ConfirmModal'
import { commonMessages } from 'app/i18n/messages'
import { JobInfoHeaderMenuMessages } from '../messages'
import { fetchJobInfoThunk, updateJobThunk } from 'features/jobs/slice'
import { JobPageContext } from 'features/jobs/JobPageContext'
import { TextField } from 'components/Fields'
import { FormContent } from './styles'
import { DOMAIN_API_ERROR } from 'features/jobs/consts'
import domainOrJobNameValidator from 'app/utils/domainOrJobNameValidator'

interface RenameJobFormValues {
  jobName: string
}

interface RenameJobModalProps {
  setIsRenameJobOpen: Dispatch<SetStateAction<boolean>>
  setError: Dispatch<SetStateAction<object | undefined>>
}

function RenameJobModal({ setIsRenameJobOpen, setError }: RenameJobModalProps) {
  const { formatMessage } = useIntl()
  const dispatch = useAppDispatch()
  const { jobName, jobId } = useContext(JobPageContext)

  const schema = Yup.object().shape({
    jobName: domainOrJobNameValidator(formatMessage),
  })
  const initialValues = { jobName }

  const handleRenameJob = async (
    { jobName }: RenameJobFormValues,
    formProps: FormikHelpers<RenameJobFormValues>
  ) => {
    try {
      await dispatch(
        updateJobThunk({ domainId: jobId, payload: { name: jobName } })
      )
      await dispatch(fetchJobInfoThunk(jobId))
      setIsRenameJobOpen(false)
    } catch (err) {
      console.log('Error renaming job', err)
      if (err?.name === DOMAIN_API_ERROR.DOMAIN_JOB_EXISTS) {
        return formProps.setFieldError(
          'jobName',
          formatMessage(
            JobInfoHeaderMenuMessages.renameModalNotUniqueErrorMessage
          )
        )
      }
      setError(err)
      setIsRenameJobOpen(false)
    }
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={schema}
      onSubmit={handleRenameJob}
    >
      {({ isSubmitting, submitForm }) => (
        <ConfirmModal
          isOpen
          onDiscard={() => {
            if (!isSubmitting) {
              setIsRenameJobOpen(false)
            }
          }}
          onConfirm={() => submitForm()}
          title={formatMessage(JobInfoHeaderMenuMessages.renameModalTitle, {
            jobName,
          })}
          discardBtnTitle={formatMessage(commonMessages.cancel)}
          confirmBtnTitle={formatMessage(commonMessages.apply)}
          variant="primary"
          isConfirmBtnDisabled={isSubmitting}
        >
          <Form>
            <FormContent>
              <TextField
                disabled={isSubmitting}
                name="jobName"
                placeholder={formatMessage(
                  JobInfoHeaderMenuMessages.renameLabel
                )}
                showLabel
                maxLength={256}
              />
            </FormContent>
          </Form>
        </ConfirmModal>
      )}
    </Formik>
  )
}

export default RenameJobModal
